import {
  DocLinkConsultTypes,
  DocLinkConsultTypeLabels,
  getDocLinkConsultTypeColor,
  getHubMedOverviewBucketColor,
} from "@/config/docLinkConsultTypes";

const hmNum = (v, fb = 0) => {
  const n = Number(v ?? fb);
  return Number.isFinite(n) ? n : fb;
};

export const hubMedConsultCount = (c) =>
  hmNum(c?.count ?? c?.Count, 0);

export const hubMedConsultLabel = (c) =>
  c?.displayName ||
  c?.DisplayName ||
  c?.consultTypeKey ||
  c?.ConsultTypeKey ||
  String(c?.phConsultType ?? c?.PHConsultType ?? "");

/** HubMed “appointments per day” stacked chart — fixed series, distinct colours. */
export const HUBMED_OVERVIEW_CHART_SERIES_KEYS = [
  "Script request",
  "Weight loss",
  "Follow up weight loss",
  "Bulk billed",
  "ADHD",
  "Smoking",
  "Cannabis",
  "Menopause",
  "Sexual health",
  "Specialist",
];

/** Stacked “appointments over time” series colours — same tokens as consult-type pie. */
export const HUBMED_OVERVIEW_DAILY_STACK_COLORS =
  HUBMED_OVERVIEW_CHART_SERIES_KEYS.map((k) => getHubMedOverviewBucketColor(k));

const normConsultLabel = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

/** Numeric DocLink consult type from breakdown item, if present. */
export function hubMedConsultTypeCodeFromItem(c) {
  const raw =
    c?.phConsultType ??
    c?.PHConsultType ??
    c?.consultType ??
    c?.ConsultType ??
    c?.docLinkConsultType ??
    c?.DocLinkConsultType;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

const buildLabelToConsultTypeCodeMap = () => {
  const m = new Map();
  Object.entries(DocLinkConsultTypeLabels).forEach(([codeStr, label]) => {
    const code = Number(codeStr);
    if (!Number.isFinite(code) || code === DocLinkConsultTypes.NONE) return;
    m.set(normConsultLabel(label), code);
  });
  m.set("follow up weight loss", DocLinkConsultTypes.FOLLOWUP_WEIGHTLOSS);
  m.set("follow-up weight loss", DocLinkConsultTypes.FOLLOWUP_WEIGHTLOSS);
  m.set("medcerts uni", DocLinkConsultTypes.MEDCERTS_UNIV);
  m.set("covid antiviral", DocLinkConsultTypes.COVID_ANTIVIRAL);
  m.set("covid-19 antiviral", DocLinkConsultTypes.COVID_ANTIVIRAL);
  return m;
};

const LABEL_TO_CONSULT_TYPE_CODE = buildLabelToConsultTypeCodeMap();

/** Resolve DocLink consult enum from API row (numeric fields or display label). */
export function resolveHubMedConsultTypeCode(c) {
  const fromNum = hubMedConsultTypeCodeFromItem(c);
  if (fromNum != null && Number.isFinite(fromNum)) return fromNum;
  const label = hubMedConsultLabel(c);
  return LABEL_TO_CONSULT_TYPE_CODE.get(normConsultLabel(label)) ?? null;
}

/**
 * Maps API consult label → overview chart bucket, or null if excluded.
 * ADHD initial + repeat → "ADHD"; cannabis initial + repeat → "Cannabis".
 * "Follow up weight loss" is matched before plain "Weight loss".
 */
export function classifyHubMedOverviewChartBucket(rawLabel) {
  const n = normConsultLabel(rawLabel);
  if (!n) return null;

  if (/follow/.test(n) && /weight/.test(n) && /loss/.test(n)) {
    return "Follow up weight loss";
  }
  if (/weight/.test(n) && /loss/.test(n)) return "Weight loss";
  if (/script/.test(n) && (/request/.test(n) || /\breq\b/.test(n))) {
    return "Script request";
  }
  if (/bulk/.test(n) && /bill/.test(n)) return "Bulk billed";
  if (/adhd/.test(n)) return "ADHD";
  if (/smok/.test(n)) return "Smoking";
  if (/cannabis/.test(n)) return "Cannabis";
  if (/menopause/.test(n) || /menopaus/.test(n)) return "Menopause";
  if (/sexual/.test(n) && /health/.test(n)) return "Sexual health";
  if (/specialist/.test(n)) return "Specialist";
  return null;
}

const dayBookingDate = (d) => d?.bookingDate ?? d?.BookingDate;

const dayConsultBreakdown = (d) =>
  d?.consultTypeBreakdown ?? d?.ConsultTypeBreakdown ?? [];

export function buildHubMedOverviewPerDayStackRows(appointmentsPerDay) {
  const keys = HUBMED_OVERVIEW_CHART_SERIES_KEYS;
  return (appointmentsPerDay || []).map((d) => {
    const row = { BookingDate: dayBookingDate(d) };
    keys.forEach((k) => {
      row[k] = 0;
    });
    dayConsultBreakdown(d).forEach((c) => {
      const label = hubMedConsultLabel(c);
      const bucket = classifyHubMedOverviewChartBucket(label);
      if (bucket && Object.prototype.hasOwnProperty.call(row, bucket)) {
        row[bucket] += hubMedConsultCount(c);
      }
    });
    return row;
  });
}

export function collectConsultTypeLabels(rangeBreakdown, appointmentsPerDay) {
  const labels = [];
  const seen = new Set();
  const push = (c) => {
    const l = hubMedConsultLabel(c);
    if (!l || seen.has(l)) return;
    seen.add(l);
    labels.push(l);
  };
  (rangeBreakdown || []).forEach(push);
  (appointmentsPerDay || []).forEach((d) =>
    dayConsultBreakdown(d).forEach(push)
  );
  return labels;
}

export function buildHubMedStackedChartRows(appointmentsPerDay, labels) {
  const keys = labels.length ? labels : ["Appointments"];
  return (appointmentsPerDay || []).map((d) => {
    const row = {
      BookingDate: dayBookingDate(d),
      TotalAppointments: hmNum(
        d?.totalAppointments ?? d?.TotalAppointments,
        0
      ),
    };
    keys.forEach((k) => {
      row[k] = 0;
    });
    dayConsultBreakdown(d).forEach((c) => {
      const l = hubMedConsultLabel(c);
      if (!l) return;
      if (!Object.prototype.hasOwnProperty.call(row, l)) row[l] = 0;
      row[l] += hubMedConsultCount(c);
    });
    if (!labels.length) {
      row.Appointments = row.TotalAppointments;
    }
    return row;
  });
}

export const HUBMED_CHART_FILLS = [
  "#7c3aed",
  "#14b8a6",
  "#f59e0b",
  "#ec4899",
  "#2563eb",
  "#22c55e",
  "#e11d48",
  "#0d9488",
];

const PIE_UNKNOWN_COLOR = "#64748b";

/**
 * Syncfusion accumulation chart segments for consult-type breakdown.
 * Colours follow `DocLinkConsultTypeColors` by resolved consult type enum.
 * @param {Array} items — camelCase or Pascal consult breakdown items
 * @returns {{ x: string, y: number, color: string }[]}
 */
export function mapConsultTypeBreakdownToPie(items) {
  const list = Array.isArray(items) ? items : [];
  const slices = list
    .map((c) => {
      const code = resolveHubMedConsultTypeCode(c);
      return {
        x: hubMedConsultLabel(c),
        y: hubMedConsultCount(c),
        color:
          code != null ? getDocLinkConsultTypeColor(code) : PIE_UNKNOWN_COLOR,
      };
    })
    .filter((d) => d.y > 0 && d.x)
    .sort((a, b) => a.x.localeCompare(b.x, undefined, { sensitivity: "base" }));

  return slices.map((d) => ({
    x: d.x,
    y: d.y,
    color: d.color,
  }));
}
